import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle2, AlertCircle, XCircle } from 'lucide-react';

const API_URL = getApiBaseUrl();

const OnboardingWidget = ({ userRole }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatus();
  }, [userRole]);

  const fetchStatus = async () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    const endpoint = userRole === 'landlord' || userRole === 'landlord_admin' 
      ? '/api/onboarding/landlord/status'
      : '/api/onboarding/tenant/status';
    
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        // Clone response before reading to avoid stream consumption issues
        const data = await response.clone().json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;

  // Check if onboarding is complete
  const isLandlord = userRole === 'landlord' || userRole === 'landlord_admin';
  
  // For landlords: need KYC + AML + agreement
  // For tenants: KYC passed OR onboarding marked as complete
  const isComplete = isLandlord 
    ? (status?.kyc_status === 'passed' && 
       status?.aml_status === 'passed' && 
       status?.management_agreement_signed)
    : (status?.kyc_status === 'passed' || status?.onboarding_completed);

  // Don't show widget if complete
  if (isComplete) {
    return (
      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg mb-6">
        <CheckCircle2 className="w-5 h-5 text-green-600" />
        <span className="text-sm font-medium text-green-800">✓ VERIFIED</span>
        <span className="text-sm text-green-700">Account fully verified</span>
      </div>
    );
  }

  // Show onboarding progress
  const steps = isLandlord ? [
    { label: 'Account Type', complete: !!status?.landlord_type, key: 'type' },
    { label: 'ID Verification', complete: status?.kyc_status === 'passed', key: 'kyc' },
    { label: 'AML Check', complete: status?.aml_status === 'passed', key: 'aml' },
    { label: 'Bank Connected', complete: status?.bank_connected, key: 'bank' },
    { label: 'Agreement Signed', complete: status?.management_agreement_signed, key: 'agreement' }
  ] : [
    { label: 'Profile', complete: status?.profile_completed, key: 'profile' },
    { label: 'ID Verification', complete: status?.kyc_status === 'passed', key: 'kyc' },
    { label: 'Right to Rent', complete: status?.right_to_rent_status === 'passed', key: 'rtr' },
    { label: 'Employment', complete: status?.employment_info_provided, key: 'employment' },
    { label: 'Credit Check', complete: status?.credit_check_consent, key: 'credit' }
  ];

  const completedSteps = steps.filter(s => s.complete).length;
  const totalSteps = steps.length;
  const progress = Math.round((completedSteps / totalSteps) * 100);

  return (
    <Card className="p-6 mb-8 border-2 border-orange-200 bg-orange-50">
      <div className="flex items-start gap-4">
        <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-orange-900 mb-2">
            Complete Your Account Setup
          </h3>
          <p className="text-sm text-orange-800 mb-4">
            You must complete all verification steps before you can {isLandlord ? 'list properties' : 'apply to properties'}.
          </p>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-xs text-orange-700 mb-1">
              <span>{completedSteps} of {totalSteps} completed</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-orange-200 rounded-full h-2">
              <div 
                className="bg-orange-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Steps Checklist */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
            {steps.map(step => (
              <div key={step.key} className="flex items-center gap-1.5">
                {step.complete ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-orange-400" />
                )}
                <span className={`text-xs ${step.complete ? 'text-green-700' : 'text-orange-700'}`}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>

          <Link to={isLandlord ? '/landlord/onboarding' : '/tenant/onboarding'}>
            <Button className="w-full sm:w-auto">
              Complete Setup Now →
            </Button>
          </Link>
        </div>
      </div>
    </Card>
  );
};

export default OnboardingWidget;
